/**
 * ActivePropertyPill — top-bar pill that shows the currently active property
 * and lets the operator switch it from anywhere in the app.
 *
 * Behavior:
 *   • Shows "Active · 22 Oak Ridge · TX ▼" when a property is selected
 *   • Shows "Pick a property ▼" when nothing is active yet
 *   • Click → dropdown with:
 *       – search box (address / city)
 *       – stage-grouped list of every workspace property
 *       – "Open Pipeline" footer link
 *   • Switching a property fires the global setter; every engine + tab
 *     re-scopes automatically because they consume the same context.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronDown, MapPin, Search, Building2, Check, Trees } from "lucide-react";
import { useActiveProperty } from "@/context/ActivePropertyContext";

const STAGE_STYLE = {
  lead:           { color: "#64748B", label: "Lead" },
  qualifying:     { color: "#3B82F6", label: "Qualifying" },
  under_contract: { color: "#8B5CF6", label: "Under contract" },
  marketing:      { color: "#F59E0B", label: "Marketing" },
  pending_close:  { color: "#EA580C", label: "Pending close" },
  closed:         { color: "#10B981", label: "Closed" },
  dead:           { color: "#EF4444", label: "Dead" },
};

function shortAddress(p) {
  if (!p) return "";
  const street = p.address?.street || "Untitled";
  const state = p.address?.state || "";
  return state ? `${street} · ${state}` : street;
}

export default function ActivePropertyPill() {
  const { properties, selected, propertyId, setPropertyId, loading } = useActiveProperty();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [anchorRect, setAnchorRect] = useState(null);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)
          && buttonRef.current && !buttonRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Recompute the anchor position on open + on window resize/scroll so
  // the portalled dropdown always sits directly under the pill button
  // regardless of scroll offset.
  useEffect(() => {
    if (!open) return undefined;
    const recompute = () => {
      if (buttonRef.current) setAnchorRect(buttonRef.current.getBoundingClientRect());
    };
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      // Focus search when opening — makes typing-to-filter feel instant.
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQuery("");
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return properties;
    const term = query.trim().toLowerCase();
    return properties.filter((p) => {
      const s = `${p.address?.street || ""} ${p.address?.city || ""} ${p.address?.state || ""} ${p.address?.zip_code || ""}`.toLowerCase();
      return s.includes(term);
    });
  }, [properties, query]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const p of filtered) {
      const stage = p.stage || "lead";
      if (!buckets[stage]) buckets[stage] = [];
      buckets[stage].push(p);
    }
    const knownOrder = Object.entries(STAGE_STYLE).map(([key, meta]) => ({
      key, meta, items: buckets[key] || [],
    }));
    // Any stage the operator has that we don't have a swatch for goes
    // into a generic "Other" bucket at the bottom — never silently drop
    // a property just because we didn't hard-code its stage color.
    const knownKeys = new Set(Object.keys(STAGE_STYLE));
    const otherItems = Object.entries(buckets)
      .filter(([k]) => !knownKeys.has(k))
      .flatMap(([, items]) => items);
    if (otherItems.length) {
      knownOrder.push({
        key: "__other__",
        meta: { color: "#64748B", label: "Other stages" },
        items: otherItems,
      });
    }
    return knownOrder.filter((b) => b.items.length > 0);
  }, [filtered]);

  return (
    <div className="relative">
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        data-testid="v3-topbar-active-property"
        className={
          "hidden md:inline-flex items-center gap-2 rounded-lg pl-2.5 pr-2 py-1.5 text-[12px] font-semibold transition-all border " +
          (selected
            ? "border-amber-300 bg-amber-50/60 text-amber-900 hover:border-amber-400 hover:bg-amber-50"
            : "border-slate-200 text-slate-500 hover:border-amber-300 hover:bg-amber-50/40")
        }
        title={selected
          ? `Active property: ${selected.address?.street || "Untitled"}`
          : "No property selected — click to pick one"}
      >
        <span
          className="h-2 w-2 rounded-full"
          style={{
            backgroundColor: selected
              ? (STAGE_STYLE[selected.stage]?.color || "#F59E0B")
              : "#CBD5E1",
          }}
        />
        <span className="text-[9.5px] uppercase tracking-widest font-black text-amber-600">
          Active
        </span>
        <span className="max-w-[180px] truncate">
          {selected ? shortAddress(selected) : "Pick a property"}
        </span>
        <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>

      {open && anchorRect && createPortal(
        <div
          ref={ref}
          data-testid="v3-topbar-active-property-menu"
          className="fixed w-[340px] rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
          style={{
            top: anchorRect.bottom + 8,
            right: Math.max(8, window.innerWidth - anchorRect.right),
            zIndex: 10000,
          }}
        >
          {/* Header */}
          <div className="px-3.5 pt-3 pb-2 border-b border-slate-100">
            <div className="text-[9.5px] uppercase tracking-widest font-black text-amber-600 mb-1">
              Active property
            </div>
            <div className="text-[13px] font-bold text-slate-900 leading-tight truncate">
              {selected ? (selected.address?.street || "Untitled") : "None selected"}
            </div>
            {selected && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 shrink-0" />
                {[selected.address?.city, selected.address?.state, selected.address?.zip_code].filter(Boolean).join(", ")}
              </div>
            )}
          </div>

          {/* Search */}
          <div className="p-2 border-b border-slate-100 bg-slate-50/60">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search address, city…"
                data-testid="v3-active-property-search"
                className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
          </div>

          {/* List */}
          <div className="max-h-[380px] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-[12px] text-slate-500">
                Loading properties…
              </div>
            ) : properties.length === 0 ? (
              <EmptyPickerState />
            ) : grouped.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-slate-500">
                No properties match “{query}”.
              </div>
            ) : (
              grouped.map((group) => (
                <div key={group.key}>
                  <div
                    className="sticky top-0 bg-white/95 backdrop-blur px-3.5 pt-2.5 pb-1 text-[9.5px] uppercase tracking-widest font-black"
                    style={{ color: group.meta.color }}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: group.meta.color }} />
                      {group.meta.label}
                      <span className="text-slate-400">· {group.items.length}</span>
                    </span>
                  </div>
                  {group.items.map((p) => {
                    const isActive = p.id === propertyId;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setPropertyId(p.id); setOpen(false); }}
                        data-testid={`v3-active-property-opt-${p.id}`}
                        className={
                          "w-full text-left flex items-start gap-2 px-3.5 py-2 text-[12.5px] transition-colors " +
                          (isActive
                            ? "bg-amber-50/70 text-amber-900"
                            : "text-slate-700 hover:bg-slate-50")
                        }
                      >
                        <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 truncate leading-tight flex items-center gap-1.5">
                            <span className="truncate">{p.address?.street || "Untitled"}</span>
                            {p.deal_type === "land" && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-[1px] text-[8.5px] font-black tracking-widest shrink-0"
                                title="Land deal"
                              >
                                <Trees className="w-2.5 h-2.5" /> LAND
                              </span>
                            )}
                          </div>
                          <div className="text-[10.5px] text-slate-500 truncate">
                            {[p.address?.city, p.address?.state, p.address?.zip_code].filter(Boolean).join(", ")}
                          </div>
                        </div>
                        {isActive && <Check className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 px-3.5 py-2 flex items-center justify-between bg-slate-50/60">
            <Link
              to="/v3/crm/pipeline"
              onClick={() => setOpen(false)}
              data-testid="v3-active-property-open-pipeline"
              className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1"
            >
              Open Pipeline
            </Link>
            {selected && (
              <Link
                to={`/v3/properties/${selected.id}`}
                onClick={() => setOpen(false)}
                data-testid="v3-active-property-open-workspace"
                className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
              >
                Command Center →
              </Link>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function EmptyPickerState() {
  return (
    <div className="p-6 text-center">
      <Building2 className="w-6 h-6 mx-auto mb-2 text-amber-500" />
      <div className="text-slate-900 font-bold text-[13px]">No properties yet</div>
      <div className="text-[11.5px] text-slate-500 mt-1">
        Add your first property from the Pipeline to start using the Intelligence Engines.
      </div>
      <Link
        to="/v3/crm/pipeline"
        className="mt-3 inline-flex items-center gap-1 rounded-lg text-white text-[11.5px] font-semibold px-2.5 py-1 shadow-sm"
        style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
      >
        Go to Pipeline
      </Link>
    </div>
  );
}
